import { parse } from 'marked';
import { createEmptyCard, fsrs, generatorParameters, Rating } from 'ts-fsrs';

/**
 * Card as parsed from disk.
 */
export interface FlashCardQuestion {
   id: string;
   prompt: string;
   answer: string;
}

/**
 * Keeping things simple for now. "Again" = forgot, "Good" = remembered.
 * NOTE: Keep in sync with REVIEW_RESULTS (below).
 */
export type ReviewResult = "again" | "good"

/**
 * NOTE: Keep in sync with ReviewResult (above).
 */
export const REVIEW_RESULTS = new Set<ReviewResult>(['again', 'good']);

/**
 * Translates internal review result type to ts-fsrs Rating type.
 */
function reviewResult2rating(rr: ReviewResult): Rating.Again | Rating.Good {
   switch (rr) {
      case 'again':
         return Rating.Again;
      case 'good':
         return Rating.Good;
      default:
         // never executed
         const r: never = rr;
   }
   // never executed
   const r: never = rr;
   return r;
}

/**
 * A single review of a card is a (ReviewResult, timestamp) tuple.
 */
export interface FlashCardReview {
   result: ReviewResult,
   date: Date,
};

/**
 * A full flash card is the question plus its review history.
 */
export interface FlashCard extends FlashCardQuestion {
   reviews: FlashCardReview[];
}

/**
 * Returns a new FlashCard instance with the added review. Passed `card` unmodified.
 */
export function makeReviewedCard(card: FlashCard, result: ReviewResult): FlashCard {
   return {
      ...card,
      reviews: [...card.reviews, { result, date: new Date() }]
   };
}

interface LoadQuestionsResult {
   questions: FlashCardQuestion[];
   problems: string[];
}

/**
 * parses raw markdown content `md` from disk into flash card (id/q/a) questions.
 */
function parseFlashCardQuestions(md: string): LoadQuestionsResult {
   const questions: FlashCardQuestion[] = [];
   const seenIDs: Set<string> = new Set();
   const questionChunks = md.split(/^={3,}$/m);  // split at 3+ "===", as only line contents
   const problems: string[] = [];
   for (let q of questionChunks) {
      const pieces = q.split("---");
      if (pieces.length != 3) {
         const msgPieces = [`Expected 3 parts for question, but got ${pieces.length}:`];
         pieces.forEach((p, i) => {
            msgPieces.push(`  ${i + 1}. "${p}"`)
         });
         const msg = msgPieces.join('\n');
         console.warn(msg);
         problems.push(msg);
         continue;
      }
      const [id, prompt_md, answer_md] = pieces.map((p) => p.trim());
      if (!id.includes("/")) {
         const msg = `Expected id (${id}) to include slash (/) but didn't. Skipping. Contents: ${q}`;
         console.warn(msg);
         problems.push(msg);
         continue;
      }
      if (seenIDs.has(id)) {
         const msg = `Already saw question ID '${id}'. Skipping.`;
         console.warn(msg);
         problems.push(msg);
         continue;
      }

      questions.push({ id, prompt: parse(prompt_md, { async: false }), answer: parse(answer_md, { async: false }) });
      seenIDs.add(id);
   }
   return { questions, problems };
}

export async function loadFlashCardQuestions(assetPath: string): Promise<LoadQuestionsResult> {
   // load and parse questions from disk
   const contents = await (await fetch(assetPath)).text();
   const lqr = parseFlashCardQuestions(contents);
   // console.log(`Got ${qs.length} Qs:`);
   // qs.forEach((q, i) => { console.log(`  ${i + 1}. [${q.id}] ${q.prompt} --- ${q.answer}`) })
   return lqr;
}

/**
 * The whole SRS thing is here. Review history -> when next review? If no
 * reviews, return 1 min ago to avoid races.
 */
export function getNextReviewDate(reviewHistory: FlashCardReview[]): Date {
   // return 1 min ago if no reviews
   if (reviewHistory.length == 0) {
      return new Date(Date.now() - 1000 * 60);
   }

   // Setup generator (probably inefficient to do every time)
   const params = generatorParameters({
      enable_fuzz: false,
      enable_short_term: true,
      learning_steps: ["10m", "6h"],
   });
   const f = fsrs(params);

   // Start with a fresh card (optionally set the creation date to the first review date)
   let card = createEmptyCard(new Date());

   // Sort reviews chronologically to be safe
   const sortedReviews = reviewHistory.toSorted((a, b) => a.date.getTime() - b.date.getTime());

   for (const review of sortedReviews) {
      // f.repeat returns an array of possible outcomes (Again, Hard, Good, Easy).
      // We select the one that actually happened in history.
      const scheduling_cards = f.repeat(card, review.date);

      // Select the schedule corresponding to the rating given at that time
      // Note: RecordLog is the log, card is the new card state
      const rating = reviewResult2rating(review.result);
      card = scheduling_cards[rating].card;
   }

   // NOTE: Could return card for any desired rendering info and have separate
   // wrapper to extract date.
   return card.due;
}

export function friendlyTimeDelta(date: Date): string {
   const now = new Date();
   const diffMs = date.getTime() - now.getTime();
   const absDiffMs = Math.abs(diffMs);
   const isPast = diffMs < 0;

   const seconds = absDiffMs / 1000;
   const minutes = seconds / 60;
   const hours = minutes / 60;
   const days = hours / 24;
   const months = days / 30.44; // Average month length
   const years = days / 365.25; // Account for leap years

   if (isPast) {
      // Past times
      if (seconds < 60) {
         return `${Math.floor(seconds)} seconds ago`;
      } else if (minutes < 60) {
         return `${Math.floor(minutes)} minutes ago`;
      } else if (hours < 24) {
         return `${Math.floor(hours)} hours ago`;
      } else if (days < 7) {
         return `${days.toFixed(1)} days ago`;
      } else if (days < 30) {
         return `${Math.floor(days)} days ago`;
      } else if (months < 3) {
         return `${months.toFixed(1)} months ago`;
      } else if (months < 12) {
         return `${Math.floor(months)} months ago`;
      } else {
         return `${years.toFixed(1)} years ago`;
      }
   } else {
      // Future times
      if (seconds < 60) {
         return `in ${Math.floor(seconds)} seconds`;
      } else if (minutes < 60) {
         return `in ${Math.floor(minutes)} minutes`;
      } else if (hours < 24) {
         return `in ${Math.floor(hours)} hours`;
      } else if (days < 7) {
         return `in ${days.toFixed(1)} days`;
      } else if (days < 30) {
         return `in ${Math.floor(days)} days`;
      } else if (months < 3) {
         return `in ${months.toFixed(1)} months`;
      } else if (months < 12) {
         return `in ${Math.floor(months)} months`;
      } else {
         return `in ${years.toFixed(1)} years`;
      }
   }
}
